import { useEffect, useRef, useState } from 'react'
import {
	AppBar,
	Box,
	IconButton,
	MenuItem,
	SwipeableDrawer,
	Toolbar,
	Typography,
} from '@mui/material'
import { Menu as MenuIcon } from '@mui/icons-material'
import { styled, useTheme } from '@mui/material/styles'
import { NavLink } from 'react-router-dom'

const iOS =
	typeof navigator !== 'undefined' &&
	/iPad|iPhone|iPod/.test(navigator.userAgent)

import logoPath from 'images/logo.png'

const StyledLogo = styled('img')`
	width: 250px;

	${({ theme }) => theme.breakpoints.up('xl')} {
		width: 400px;
	}
`

const StyledTypography = styled(Typography)(({ theme }) => ({
	fontSize: '0.6rem',
	fontWeight: '700',
	margin: `0 ${theme.spacing(1)}`,
	textTransform: 'uppercase',
	'&:last-child': {
		marginRight: 0,
	},
	[theme.breakpoints.up('md')]: {
		fontSize: '0.6rem',
		margin: `0 ${theme.spacing(1)}`,
	},
	[theme.breakpoints.up('lg')]: {
		fontSize: '0.9rem',
		margin: `0 ${theme.spacing(1.8)}`,
	},
}))

const StyledNavLink = styled(NavLink)(({ theme }) => ({
	display: 'block',
	textDecoration: 'none',
	textAlign: 'center',
	color: theme.palette.primary.main,
	transition: '250ms',
	'&::after': {
		content: '""',
		display: 'block',
		margin: '0 auto',
		marginTop: theme.spacing(0.5),
		width: '0%',
		height: theme.spacing(0.5),
		opacity: 0,
		backgroundColor: theme.palette.primary.main,
		transition: '250ms',
	},
	'&:hover': {
		'&::after': {
			width: '100%',
			opacity: 1,
		},
	},
	[theme.breakpoints.down('md')]: {
		textAlign: 'left',
	},
}))

const pages = [
	{
		label: (
			<>
				Equip details
				<br />
				Search
			</>
		),
		path: '/',
	},
]

export default function Navigation() {
	const theme = useTheme()

	const appBarRef = useRef()

	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
	const [appBarHeight, setAppBarHeight] = useState(0)

	const handleMobileMenuOpen = () => setIsMobileMenuOpen(true)
	const handleMobileMenuClose = () => setIsMobileMenuOpen(false)

	useEffect(() => {
		const setAppBarHeightOnResize = () =>
			setAppBarHeight(appBarRef.current?.clientHeight)
		setAppBarHeightOnResize()

		window.addEventListener('resize', setAppBarHeightOnResize)
		return () => window.removeEventListener('resize', setAppBarHeightOnResize)
	}, [appBarRef])

	return (
		<>
			<AppBar
				position="sticky"
				ref={appBarRef}
				sx={(theme) => ({
					backgroundColor: theme.palette.common.white,
					borderBottom: {
						xs: `${theme.spacing(0.5)} solid ${theme.palette.primary.main}`,
						md: `${theme.spacing(1)} solid ${theme.palette.primary.main}`,
					},
					zIndex: (theme) => theme.zIndex.drawer + 1,
				})}
			>
				<Box sx={{ py: { xs: 1, xl: 3 } }}>
					<Toolbar
						sx={{
							width: '100%',
							maxWidth: '1400px',
							margin: '0 auto',
						}}
					>
						<NavLink href="/">
							<StyledLogo src={logoPath} alt="Logo" />
						</NavLink>

						<Box
							sx={{
								display: { xs: 'none', md: 'flex' },
								alignItems: 'center',
								flex: '1 0 auto',
								justifyContent: 'right',
							}}
						>
							{pages.map(({ label, path }, index) => (
								<StyledTypography key={`${index}${path}`}>
									<StyledNavLink to={path}>{label}</StyledNavLink>
								</StyledTypography>
							))}
						</Box>

						<Box
							sx={{
								marginLeft: 'auto',
								display: { xs: 'flex', md: 'none' },
							}}
						>
							<IconButton
								size="large"
								aria-label="main menu"
								aria-controls="menu-appbar"
								aria-haspopup="true"
								onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
								color="inherit"
							>
								<MenuIcon />
							</IconButton>
						</Box>
					</Toolbar>
				</Box>
			</AppBar>

			<SwipeableDrawer
				anchor="left"
				open={
					isMobileMenuOpen && theme.breakpoints.values.md > window.innerWidth
				}
				onClose={handleMobileMenuClose}
				onOpen={handleMobileMenuOpen}
				disableBackdropTransition={!iOS}
				disableDiscovery={iOS}
			>
				<Box sx={{ paddingTop: `${appBarHeight}px` }} pb={2} />
				{pages.map(({ label, path }) => (
					<MenuItem key={path} onClick={handleMobileMenuClose}>
						<StyledTypography key={path}>
							<StyledNavLink to={path}>{label}</StyledNavLink>
						</StyledTypography>
					</MenuItem>
				))}
			</SwipeableDrawer>
		</>
	)
}

